<template>
  <v-container id="thaid-callback" fluid tag="section" class="text_google">
    <v-row justify="center" align="center" style="min-height: 60vh">
      <v-col cols="12" md="6" lg="4">
        <base-material-card color="primary">
          <template v-slot:heading>
            <div class="font-weight-light text-center">
              <h3>ยืนยันตัวตนผ่าน ThaiID</h3>
            </div>
          </template>

          <v-card-text class="text-center py-8">
            <template v-if="status === 'working'">
              <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
              <p class="mt-4">กำลังตรวจสอบผลการยืนยันตัวตน...</p>
            </template>

            <template v-else-if="status === 'success'">
              <v-icon color="success" size="56">mdi-check-circle</v-icon>
              <p class="mt-4">{{ successMessage }}</p>
            </template>

            <template v-else>
              <v-icon color="error" size="56">mdi-alert-circle</v-icon>
              <p class="mt-4">{{ errorMessage }}</p>
              <v-btn color="primary" rounded to="/pages/login" class="mt-2">
                กลับหน้าเข้าสู่ระบบ
              </v-btn>
            </template>
          </v-card-text>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// ThaidCallback.vue — ปลายทางหลัง DOPA redirect กลับมา (callback_thiaid.php เป็นคนส่งมาที่นี่)
// รับ ?claim=... (สำเร็จ) หรือ ?error=... (ล้มเหลว/ยกเลิก) แล้วแลก claim เป็น session จริง
// ผ่าน thaid_claim.exchange.php ครั้งเดียว จากนั้นเก็บ localStorage แบบเดียวกับ login เดิมทุกจุด
const VALID_PERSONNEL_STATUSES = [
  "teacher", "se_director", "director", "S", "38", "3801", "3802", "3803", "C", "gov_em"
];
const VALID_ETC_M_TYPES = [
  "governmentteach", "governmentsp", "government", "temporary", "temporary_job", "permanent"
];
const USER_REDIRECT_MAP = {
  A: "/admin",
  B: "/college",
  C: "/iven",
  D: "/personnel",
  G: "/vecprovince",
  S: "/supervisions"
};

const ERROR_MESSAGES = {
  // ค่าที่ไม่รู้จัก/ไม่อยู่ใน whitelist ของ callback_thiaid.php จะตกมาที่นี่ (fallback ทั่วไป)
  thaid_denied: "การยืนยันตัวตนผ่าน ThaiID ไม่สำเร็จ กรุณาเข้าสู่ระบบใหม่",
  // ค่าจาก DOPA โดยตรง ตรงตามเอกสาร "API ลงทะเบียนสำหรับผู้ให้บริการ (RP)" รุ่น 1.2.0 หัวข้อ 7 —
  // ผ่าน whitelist ใน callback_thiaid.php แล้ว (ไม่ใช่ generic OAuth2 list — DOPA ใช้ 'user_denied'
  // สำหรับกรณีผู้ใช้กดยกเลิกจริงๆ ไม่ใช่ 'access_denied' ซึ่งความหมายจริงคือ "ไม่มีสิทธิ์เข้าถึง")
  thaid_user_denied: "ท่านยกเลิกการยืนยันตัวตนผ่าน ThaiID กรุณาเข้าสู่ระบบใหม่",
  thaid_unauthorized_client: "ระบบยืนยันตัวตน ThaiID ยังไม่ได้รับอนุญาตให้ใช้งานจากกรมการปกครอง (unauthorized_client) — กรุณาแจ้งผู้ดูแลระบบให้ตรวจสอบการลงทะเบียน client_id/redirect_uri กับ DOPA",
  thaid_invalid_client: "ไม่พบ client_id ที่ลงทะเบียนไว้กับ DOPA (invalid_client) — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_access_denied: "บัญชีนี้ไม่มีสิทธิ์เข้าถึงบริการยืนยันตัวตนของ DOPA กรุณาติดต่อผู้ดูแลระบบ",
  thaid_verify_permission_denied: "หน่วยงานยังไม่มีสิทธิ์ใช้การยืนยันตัวตนด้วยภาพใบหน้าจาก DOPA — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_invalid_request: "คำขอไปยัง ThaiID ไม่ถูกต้อง (invalid_request) — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_invalid_scope: "ขอบเขตข้อมูล (scope) ที่ขอจาก ThaiID ไม่ถูกต้อง — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_unsupported_response_type: "รูปแบบคำขอไปยัง ThaiID ไม่ได้รับการรองรับ (unsupported_response_type) — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_unsupported_grant_type: "รูปแบบการขอ token ไม่ได้รับการรองรับ — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_unsupported_token_hint: "รูปแบบคำขอ token ไม่ได้รับการรองรับ — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_unsupported_token_type: "ประเภท token ไม่ได้รับการรองรับ — กรุณาแจ้งผู้ดูแลระบบ",
  thaid_duplicate_request: "มีการส่งคำขอซ้ำ กรุณาเข้าสู่ระบบใหม่อีกครั้ง",
  thaid_invalid_refresh_token: "การยืนยันตัวตนหมดอายุ กรุณาเข้าสู่ระบบใหม่",
  thaid_server_error: "ระบบ ThaiID ของกรมการปกครองขัดข้องชั่วคราว กรุณาลองใหม่อีกครั้ง",
  // ค่าจากฝั่ง callback_thiaid.php เอง (ไม่ใช่จาก DOPA โดยตรง)
  missing_code_or_state: "ข้อมูลจาก ThaiID ไม่ครบถ้วน กรุณาเข้าสู่ระบบใหม่",
  invalid_or_expired_state: "ลิงก์ยืนยันตัวตนหมดอายุหรือถูกใช้ไปแล้ว กรุณาเข้าสู่ระบบใหม่",
  token_exchange_failed: "เชื่อมต่อระบบ ThaiID ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
  identity_verification_failed: "ไม่สามารถยืนยันตัวตนผ่าน ThaiID ได้ กรุณาลองใหม่อีกครั้ง",
  identity_mismatch: "ThaiID ที่ใช้ยืนยันไม่ตรงกับบัญชีนี้ กรุณาติดต่อผู้ดูแลระบบ",
  thaid_already_linked: "ThaiID นี้ถูกผูกกับบัญชีอื่นไปแล้ว กรุณาติดต่อผู้ดูแลระบบ",
  unknown_subject_type: "ไม่รู้จักประเภทบัญชีนี้ กรุณาติดต่อผู้ดูแลระบบ"
};

export default {
  data() {
    return {
      status: "working",
      successMessage: "",
      errorMessage: ""
    };
  },
  async mounted() {
    const { claim, error } = this.$route.query;

    if (error) {
      this.status = "error";
      this.errorMessage = ERROR_MESSAGES[error] || "เกิดข้อผิดพลาดในการยืนยันตัวตน กรุณาเข้าสู่ระบบใหม่";
      return;
    }

    if (!claim) {
      this.status = "error";
      this.errorMessage = "ไม่พบข้อมูลยืนยันตัวตน กรุณาเข้าสู่ระบบใหม่";
      return;
    }

    try {
      const result = await this.$http.post("thaid_claim.exchange.php", { claim });

      if (!result.data || !result.data.session_token) {
        this.status = "error";
        this.errorMessage = "ลิงก์ยืนยันตัวตนหมดอายุหรือถูกใช้ไปแล้ว กรุณาเข้าสู่ระบบใหม่";
        return;
      }

      const user = result.data;
      user.system_lock = false;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("force_password_change", user.must_change_password ? "1" : "0");

      const target = this.resolveTarget(user);
      if (!target) {
        localStorage.clear();
        this.status = "error";
        this.errorMessage = "บัญชีนี้ยังไม่รองรับการเข้าสู่ระบบผ่านหน้านี้ กรุณาติดต่อผู้ดูแลระบบ";
        return;
      }

      this.status = "success";
      this.successMessage = user.thaid_first_link
        ? "ผูกบัญชีนี้กับ ThaiID ของคุณเรียบร้อยแล้ว กำลังเข้าสู่ระบบ..."
        : "ยืนยันตัวตนสำเร็จ กำลังเข้าสู่ระบบ...";

      setTimeout(() => {
        this.$router.push(target);
      }, 1200);
    } catch (e) {
      this.status = "error";
      this.errorMessage = "เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาเข้าสู่ระบบใหม่";
    }
  },
  methods: {
    resolveTarget(user) {
      switch (user.thaid_subject_type) {
        case "personnel_temporary":
          return VALID_PERSONNEL_STATUSES.includes(user.user_status) ? "/user" : null;
        case "personnel_etc":
          return VALID_ETC_M_TYPES.includes(user.personnel_etc_m_type) ? "/userEtc" : null;
        case "assistantteach":
          return user.user_status === "teach" ? "/assistantTeach" : null;
        case "user":
          return USER_REDIRECT_MAP[user.user_status] || null;
        default:
          // veccentraldep/veccentral: ยังไม่มีหน้า login ของ Vue เรียกใช้ subject_type เหล่านี้
          // (ดู M6 ในแผน) — ตกมาที่นี่แปลว่ายังไม่รองรับการ redirect ต่อในหน้านี้
          return null;
      }
    }
  }
};
</script>

<style>
.text_google {
  font-family: "Kanit", sans-serif;
}
</style>
